import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { X, LogOut, Bell, Key, AlertTriangle, Settings } from 'lucide-react';
import '../styles/top-bar.css';
import logoImg from '../assets/images/tripnour_logo.png';
import { uploadLeadsApi } from '../services/api';
import { HitlNotificationContext } from '../context/HitlNotificationContext';
import { getLeadDisplayName } from '../utils/nameUtils';

const TopBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { notifications, unreadCount, toast } = useContext(HitlNotificationContext);
  const [activeToast, setActiveToast] = useState(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const notificationRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const handleAddLeadsClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      await uploadLeadsApi(file);
      setUploadMessage('Excel uploaded successfully');
      setTimeout(() => {
        setUploadMessage('');
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Failed to upload file:', error);
      setUploadMessage('Failed to upload');
      setTimeout(() => setUploadMessage(''), 3000);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset file input
      }
    }
  };

  const getAgentDetails = () => {
    let name = 'Agent';
    let emailStr = '';
    let roleStr = 'user';

    const getSafeItem = (key) => {
      const val = localStorage.getItem(key);
      if (!val) return '';
      try {
        // Automatically unwraps stringified values like '"Agent"' to 'Agent'
        const parsed = JSON.parse(val);
        return typeof parsed === 'string' ? parsed : val;
      } catch (e) {
        return val;
      }
    };

    try {
      // 1. Try reading string values if stored directly
      const localFullName = getSafeItem('full_name');
      const localUsername = getSafeItem('username');
      const localEmail = getSafeItem('email');
      const localRole = getSafeItem('role');

      if (localFullName) {
        name = localFullName;
      } else if (localUsername) {
        name = localUsername;
      }
      if (localEmail) {
        emailStr = localEmail;
      }
      if (localRole) {
        roleStr = localRole;
      }

      // 2. Fallback to 'user' object if stored as stringified JSON
      if (!localUsername || !localEmail || !localRole) {
        const userObjStr = localStorage.getItem('user');
        if (userObjStr) {
          const userObj = JSON.parse(userObjStr);
          if (userObj?.username && name === 'Agent') {
            name = userObj.username;
          }
          if (userObj?.email && !emailStr) {
            emailStr = userObj.email;
          }
          if (userObj?.role && roleStr === 'user') {
            roleStr = userObj.role;
          }
        }
      }
    } catch (e) {
      console.error("Error reading user details from localStorage:", e);
    }

    const stripQuotes = (str) => {
      if (!str || typeof str !== 'string') return '';
      return str.replace(/^["']|["']$/g, '');
    };

    return {
      name: stripQuotes(name),
      email: stripQuotes(emailStr),
      role: stripQuotes(roleStr)
    };
  };

  const { name: agentName, email: agentEmail, role: agentRole } = getAgentDetails();

  // Extract display name for initials
  const displayName = agentName && agentName.includes('@') ? agentName.split('@')[0] : agentName;

  const getInitials = (name) => {
    if (!name || name === 'Agent') return 'RK';
    const parts = name.split(/[._-]/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(displayName);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (toast) {
      setActiveToast(toast);
      const timer = setTimeout(() => {
        setActiveToast(null);
      }, 7000); // Float for 4 seconds
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    const handleNavigateEvent = (event) => {
      const { leadId, tripId, hitlId, leadName, leadEmail, notification } = event.detail || {};
      handleNotificationClick(hitlId, leadId, tripId, notification || { lead_name: leadName, lead_email: leadEmail });
    };

    window.addEventListener('navigate-to-hitl', handleNavigateEvent);
    return () => {
      window.removeEventListener('navigate-to-hitl', handleNavigateEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProfileClick = () => {
    if (window.innerWidth <= 768) {
      setIsMenuOpen(!isMenuOpen);
    } else {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleNotificationClick = (hitlId, leadId, tripId, notif = {}) => {
    // We intentionally do NOT call markAsRead(hitlId) here because simply clicking 
    // the notification doesn't mean the agent actually resolved the HITL task yet.
    setIsNotificationOpen(false);
    const resolvedName = getLeadDisplayName(notif.lead_name, notif.lead_email || notif.email || (leadId && String(leadId).includes('@') ? leadId : ''));
    // Navigate to the correct route with clean URL, but pass required data via router state
    navigate(`/lead-details/${leadId}`, {
      state: { 
        tripId, 
        hitlId, 
        hasHitl: true,
        leadName: resolvedName,
        leadEmail: notif.lead_email || notif.email
      }
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsDropdownOpen(false);
    setIsMenuOpen(false);
    navigate('/signin');
  };

  return (
    <header className="top-bar">
      <div className="top-bar-left">
        <div className="brand-info" onClick={() => navigate('/')}>
          <img src={logoImg} alt="Logo" className="logo-img" />
          <p className='font-10-normal dashboard-main-highlight tagline'>AI-Driven Engagement Platform</p>
        </div>

        <nav className={`top-bar-nav font-16 ${isMenuOpen ? 'mobile-open' : ''}`}>
          <button className="mobile-close-btn" onClick={() => setIsMenuOpen(false)}>
            <X size={24} />
          </button>
          <a href="#dashboard" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} onClick={(e) => {
            e.preventDefault();
            setIsMenuOpen(false);
            navigate('/');
          }}>Dashboard</a>
          <a href="#products" className={`nav-link ${location.pathname === '/products' ? 'active' : ''}`} onClick={(e) => {
            e.preventDefault();
            setIsMenuOpen(false);
            navigate('/products');
          }}>Products</a>
          <a href="#leads" className="nav-link disabled" onClick={(e) => {
            e.preventDefault();
            setIsMenuOpen(false);
          }}>My Leads</a>
          {agentRole && agentRole.toLowerCase() === 'admin' && (
            <>
              <a href="#reports" className={`nav-link ${location.pathname === '/reports' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                navigate('/reports');
              }}>Reports</a>
              <a href="#admin-settings" className={`nav-link ${location.pathname === '/admin/settings' || location.pathname === '/settings' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                navigate('/admin/settings');
              }}>Org Settings</a>
            </>
          )}
          <button className="mobile-logout-btn" onClick={handleLogout}>
            <LogOut size={18} className="mobile-logout-icon" />
            Log Out
          </button>
        </nav>
      </div>

      <div className="top-bar-right">
        {uploadMessage && (
          <span className={`upload-message-status ${uploadMessage.includes('Failed') ? 'status-error' : 'status-success'}`}>
            {uploadMessage}
          </span>
        )}
        <input
          type="file"
          accept=".xlsx, .xls, .csv"
          ref={fileInputRef}
          className="hidden-file-input"
          onChange={handleFileChange}
        />
        <button
          onClick={handleAddLeadsClick}
          disabled={isUploading}
          className="action-btn chat"
        >
          {isUploading ? 'Uploading...' : '+ Add Leads'}
        </button>

        <div className="notification-container" ref={notificationRef}>
          <button
            className={`notification-bell-btn ${unreadCount > 0 ? 'hitl-alert-unread' : 'hitl-alert-read'}`}
            onClick={() => setIsNotificationOpen(!isNotificationOpen)}
          >
            <AlertTriangle size={18} />
            <span className="hitl-alert-text">HITL Alerts</span>
            {unreadCount > 0 && (
              <span className="notification-badge notification-badge-topbar">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>

          {isNotificationOpen && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">No new notifications</div>
                ) : (
                  notifications.map(n => {
                    const displayName = getLeadDisplayName(n.lead_name, n.lead_email || n.email || (n.lead_id?.includes('@') ? n.lead_id : ''));
                    return (
                      <div
                        key={n.hitl_id}
                        className="notification-item"
                        onClick={() => handleNotificationClick(n.hitl_id, n.lead_id, n.trip_id, n)}
                      >
                        <div className="notification-item-header">
                          <div className="notification-icon-wrapper">
                            <Bell size={14} className="notification-icon-small" />
                          </div>
                          <span className="notification-action">{n.category_code}</span>
                        </div>
                        <div className="notification-item-body">
                          <span className="notification-lead-name">Action required for lead <strong>{displayName}</strong></span>
                          {n.priority && (
                            <span className={`notification-priority priority-${n.priority.toLowerCase()}`}>
                              {n.priority}
                            </span>
                          )}
                        </div>
                        <div className="notification-item-footer">
                          <span className="notification-time">{new Date(n.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
        <div className="profile-container" ref={dropdownRef}>
          <div className="user-profile" onClick={handleProfileClick}>
            {initials}
          </div>
          {isDropdownOpen && (
            <div className="profile-dropdown">
              <div className="dropdown-user-info">
                <span className="dropdown-username">{agentEmail || agentName}</span>
                <span className="dropdown-role">{agentRole || 'User'}</span>
              </div>
              {agentRole && agentRole.toLowerCase() === 'admin' && (
                <button className="dropdown-item" onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/admin/settings');
                }}>
                  <Settings size={16} />
                  <span>Organization Settings</span>
                </button>
              )}
              <button className="dropdown-item" onClick={() => {
                setIsDropdownOpen(false);
                navigate('/change-password');
              }}>
                <Key size={16} />
                <span>Change Password</span>
              </button>
              <button className="dropdown-item logout" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Log Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {activeToast && (
        <div
          className="hitl-toast"
          onClick={() => {
            handleNotificationClick(activeToast.hitl_id, activeToast.lead_id, activeToast.trip_id, activeToast);
            setActiveToast(null);
          }}
        >
          <button className="toast-close" onClick={(e) => { e.stopPropagation(); setActiveToast(null); }}>
            <X size={14} />
          </button>
          <div className="toast-header">
            <div className="notification-icon-wrapper">
              <Bell size={14} className="notification-icon-small" />
            </div>
            <span className="toast-action">{activeToast.category_code}</span>
            {activeToast.priority && (
              <span className={`notification-priority priority-${activeToast.priority.toLowerCase()} notification-priority-toast`}>
                {activeToast.priority}
              </span>
            )}
          </div>
          <div className="toast-body">
            <span className="toast-lead-name">Action required for lead <strong>{getLeadDisplayName(activeToast.lead_name, activeToast.lead_email || activeToast.email || (activeToast.lead_id?.includes('@') ? activeToast.lead_id : ''))}</strong></span>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopBar;
